/**
 * PushButton — the green "Push Now" button at the top of the tools panel.
 *
 * Three visual states:
 *   - idle  → cloud-up icon, dark green gradient
 *   - busy  → spinner, dark green gradient
 *   - done  → check mark, bright green gradient (auto-revert after 1.5s)
 *
 * Calls `useSyncStore.getState().push()` directly so the parent doesn't
 * have to wire any callbacks. The CSS spin keyframes are injected once
 * on first mount via `injectSpinKeyframes()`.
 */
import { useState } from 'react'
import { useSyncStore } from '../../stores/sync'

/** Inject the @keyframes for the busy-state spinner exactly once. */
function injectSpinKeyframes() {
  if (typeof document === 'undefined') return
  if (document.getElementById('tp-spin-style')) return
  const s = document.createElement('style')
  s.id = 'tp-spin-style'
  s.textContent = '@keyframes tp-spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}'
  document.head.appendChild(s)
}

export default function PushButton() {
  injectSpinKeyframes()

  const [state, setState] = useState<'idle' | 'busy' | 'done'>('idle')
  const [hov, setHov] = useState(false)

  async function go() {
    if (state !== 'idle') return
    setState('busy')
    try {
      await useSyncStore.getState().push()
    } catch {
      /* silent — user will see SyncBar error state */
    }
    setState('done')
    setTimeout(() => setState('idle'), 1500)
  }

  const gradient = state === 'done' ? '#4ade80,#22c55e' : '#059669,#047857'
  const shadow   = state === 'done' ? '74,222,128'     : '5,150,105'

  return (
    <div
      onClick={go}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 4,
        cursor: 'pointer',
      }}
    >
      <div
        onMouseEnter={() => setHov(true)}
        onMouseLeave={() => setHov(false)}
        style={{
          width: 48,
          height: 48,
          borderRadius: '50%',
          background: `linear-gradient(135deg,${gradient})`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: `0 4px 16px rgba(${shadow},0.45)`,
          transform: hov ? 'scale(1.12)' : 'scale(1)',
          transition: 'transform 0.18s, background 0.3s',
          flexShrink: 0,
        }}
      >
        {state === 'busy' ? (
          <div
            style={{
              width: 20,
              height: 20,
              border: '3px solid rgba(255,255,255,0.3)',
              borderTopColor: '#fff',
              borderRadius: '50%',
              animation: 'tp-spin 0.8s linear infinite',
            }}
          />
        ) : state === 'done' ? (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        ) : (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="16 16 12 12 8 16" />
            <line x1="12" y1="12" x2="12" y2="21" />
            <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
          </svg>
        )}
      </div>
      <div style={{ fontSize: 9, color: 'var(--text-muted)', fontWeight: 600 }}>
        Push Now
      </div>
    </div>
  )
}
